import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  Timeline, TimelineItem, TimelineSeparator, TimelineDot, TimelineConnector,
  TimelineContent, TimelineOppositeContent,
} from '@mui/lab';
import { useApp } from '../context/AppContext';
import { auditApi, AuditEntry } from '../api/audit';

const CHANGE_TYPE_COLORS: Record<string, 'success' | 'error' | 'info' | 'warning'> = {
  create: 'success',
  delete: 'error',
  update: 'info',
  env_toggle: 'warning',
};

export default function AuditLogPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectProject } = useApp();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      selectProject(projectId);
      setLoading(true);
      auditApi.list(projectId)
        .then(setEntries)
        .catch((e: Error) => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [projectId, selectProject]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;

  return (
    <Box maxWidth={800}>
      <Typography variant="h5" fontWeight={700} mb={3}>Audit Log</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {entries.length === 0 ? (
        <Typography color="text.secondary">No audit entries yet.</Typography>
      ) : (
        <Timeline position="right">
          {entries.map((entry, i) => (
            <TimelineItem key={`${entry.timestamp}-${entry.flagId}`}>
              <TimelineOppositeContent sx={{ flex: 0.3, py: '12px' }}>
                <Typography variant="caption" color="text.secondary">
                  {new Date(entry.timestamp).toLocaleString()}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot color={CHANGE_TYPE_COLORS[entry.changeType] || 'grey'} />
                {i < entries.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px' }}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip label={entry.changeType} size="small" color={CHANGE_TYPE_COLORS[entry.changeType]} />
                    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                      {entry.flagKey}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">{entry.changedBy}</Typography>
                  {Object.entries(entry.diff).map(([field, { before, after }]) => (
                    <Box key={field} sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">{field}: </Typography>
                      <Typography variant="caption" color="error.main" sx={{ textDecoration: 'line-through', mr: 0.5 }}>
                        {String(before)}
                      </Typography>
                      <Typography variant="caption" color="success.main">{String(after)}</Typography>
                    </Box>
                  ))}
                </Card>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Box>
  );
}
